import { useCallback, useEffect, useRef, useState } from 'react';

type SetIntervalReturn = ReturnType<typeof setInterval>;

type UseCountdownOptions = {
  interval?: number;
  autostart?: boolean;
}

const useCountdown = (initialTime: number, options?: UseCountdownOptions) => {
  const { autostart = false, interval = 1000 } = options || {};

  const [time, setTime] = useState(initialTime);
  const [counting, setCounting] = useState(autostart);

  const intervalRef = useRef<SetIntervalReturn | null>(null);

  const start = useCallback(() => setCounting(true), []);

  const pause = useCallback(() => setCounting(false), []);

  const reset = useCallback(() => {
    setTime(initialTime);
    setCounting(false);
  }, [initialTime]);

  const restart = useCallback(() => {
    setTime(initialTime);
    setCounting(true);
  }, [initialTime]);

  useEffect(() => {
    if (!counting) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);

        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(intervalRef.current as SetIntervalReturn);

          intervalRef.current = null;

          setCounting(false);

          return 0;
        }

        return prevTime - 1;
      });
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [counting, interval]);

  return {
    time,
    counting,
    start,
    pause,
    reset,
    restart,
  };
};

export default useCountdown;
